// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartDiscountPercentDisplay from "@guestsupply/App/Components/Cart/CartDiscountPercentDisplay";
import CartLeaseAgreementDisplay from "@guestsupply/App/Components/Cart/CartLeaseAgreementDisplay";
import CartShippingSummaryDisplay from "@guestsupply/App/Components/Cart/CartShippingSummaryDisplay";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import CartSaveOrderButton from "@insite/content-library/Widgets/Cart/CartSaveOrderButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LoadingOverlay, Typography } from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import get from "@insite/mobius/utilities/get";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { set } from "js-cookie";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    includeActions = "includeActions",
}

const mapStateToProps = (state: ApplicationState) => {
    const promotionsDataView = getCurrentPromotionsDataView(state);
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        cartSettings: { onePageCheckout, showTaxAndShipping },
    } = getSettingsCollection(state);

    return {
        isLoading: isCartLoading,
        cart,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showTaxAndShipping,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            isCartCheckoutDisabled(cart) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest,
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        isPunchOutOrder: cart?.properties?.isPunchout?.toLowerCase() === "true",
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        isPunchoutButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitRequisitionButtonDisabled: isCartLoading || isUpdatingCart,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        enableCancelPunchout: (getSettingsCollection(state).customSettings as any)?.enableCancelPunchout,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    cancelPunchOut,
    submitRequisition,
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeActions]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    checkoutButton?: ButtonPresentationProps;
    cancelPunchOutButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    quoteRequiredModal?: ModalPresentationProps;
    submitRequisitionButton?: ButtonPresentationProps;
    // XNG-CHANGES
    totalWrapper?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    closeButton?: InjectableCss;
}

interface ShipmentGroupingInfo {
    freeShippingThreshold: number;
    remainingAmountToFreeShipping: number;
}

export const cartTotalStyles: CartTotalStyles = {};

const styles = cartTotalStyles;

const CartTotal = ({
    showTaxAndShipping,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    preloadCheckoutShippingData,
    history,
    canCheckoutWithCart,
    isCartCheckoutDisabled,
    isCartEmpty,
    isLoading,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    canSubmitForQuote,
    rfqRequestQuotePageUrl,
    isPunchOutOrder,
    isPunchoutButtonDisabled,
    isSubmitForQuoteButtonDisabled,
    isSubmitRequisitionButtonDisabled,
    doPunchOutCheckout,
    cancelPunchOut,
    canSubmitRequisition,
    submitRequisition,
    requisitionConfirmationPageUrl,
    fields,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    // XNG-CHANGES
    homePageUrl,
    enableCancelPunchout,
    session,
    myAccountPageUrl,
    isPreloadingData,
}: Props) => {
    const [displayShippingThresholdWarning, setDisplayShippingThresholdWarning] = useState(false);
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const [shipmentGroupingInfo, setShipmentGroupingInfo] = useState<ShipmentGroupingInfo>();
    const [isOpen, setIsOpen] = useState(false);
   
    const checkoutHandler = () => {
        if (!cart) {
            return;
        }

        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe) {
                signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
            } else {
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                );
            }
            return;
        }

        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const cancelPunchOutHandler = () => {
        cancelPunchOut();
    };

    const submitForQuoteClickHandler = () => {
        if (rfqRequestQuotePageUrl) {
            history.push(rfqRequestQuotePageUrl);
        }
    };
    // XNG-CHANGES
    const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Quote") : translate("Request a Quote");

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const onClose = () => {
        setIsOpen(false);
    };

    const checkThreshold = () => {
        if (displayShippingThresholdWarning && shipmentGroupingInfo) {
            setIsOpen(true);
        } else {
            checkoutHandler();
        }
    }

    useEffect(() => {
        if (!cart) {
            return;
        }

        const displayProperty = cart.properties?.displayShippingThresholdWarning?.toLowerCase() === "true";
        setDisplayShippingThresholdWarning(displayProperty);

        if (displayProperty && cart.properties?.shipmentGroupingInfo) {
            const shipmentProperty = JSON.parse(cart.properties?.shipmentGroupingInfo);
            setShipmentGroupingInfo(shipmentProperty);
        }
    }, [cart]);

    if (!cart || cart?.cartLines?.length === 0) {
        return (
            <Button
                {...styles.submitQuoteButton}
                onClick={() => {
                    const defaultUrl = session.dashboardIsHomepage ? myAccountPageUrl : homePageUrl;
                    history.push(defaultUrl!);
                }}
                data-test-selector="cartTotal_contineShopping"
                aria-label="Continue Shopping"
            >
                {translate("Continue Shopping")}
            </Button>
        );
    }

    return (
        <LoadingOverlay loading={isLoading || isPreloadingData}>
            <StyledWrapper {...styles.totalWrapper}>
                <CartTotalDisplay
                    showTaxAndShipping={showTaxAndShipping}
                    extendedStyles={styles.cartTotal}
                    isCartEmpty={isCartEmpty}
                    cart={cart}
                    orderPromotions={orderPromotions}
                    shippingPromotions={shippingPromotions}
                    discountTotal={discountTotal}
                    //  XNG-CHANGES
                    {...{ isCartPage: true }}
                />
                <CartShippingSummaryDisplay
                    extendedStyles={styles.cartTotal}
                    isCartEmpty={isCartEmpty}
                    cart={cart}
                    {...{ isCartPage: true }}
                />
                <CartDiscountPercentDisplay
                    extendedStyles={styles.cartTotal}
                    isCartEmpty={isCartEmpty}
                    cart={cart}
                    {...{ isCartPage: true }}
                />
                <CartLeaseAgreementDisplay
                    extendedStyles={styles.cartTotal}
                    isCartEmpty={isCartEmpty}
                    cart={cart}
                    {...{ isCartPage: true }}
                />
            </StyledWrapper>
            {/* XNG-CHANGES : Chaning labels and match functionality with classic */}
            {fields.includeActions !== false && (
                <>
                    <Button
                        {...styles.checkoutButton}
                        onClick={checkThreshold}
                        disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts || !canCheckoutWithCart}
                        data-test-selector="cartTotal_checkout"
                        aria-label={
                            isPunchOutOrder
                                ? translate("Punchout")
                                : cart?.requiresApproval
                                    ? translate("Checkout for Approval")
                                    : translate("Checkout")
                        }
                    >
                        {isPunchOutOrder
                            ? translate("Punchout")
                            : cart?.requiresApproval
                                ? translate("Checkout for Approval")
                                : translate("Checkout")}
                    </Button>
                    {enableCancelPunchout && isPunchOutOrder && (
                        <Button
                            {...styles.cancelPunchOutButton}
                            disabled={isPunchoutButtonDisabled}
                            onClick={cancelPunchOutHandler}
                            aria-label="Cancel PunchOut"
                        >
                            {translate("Cancel PunchOut")}
                        </Button>
                    )}
                    <TwoButtonModal
                        headlineText=""
                        modalIsOpen={quoteRequiredModalIsOpen}
                        messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Continue")}
                        onCancel={() => setQuoteRequiredModalIsOpen(false)}
                        onSubmit={checkoutHandler}
                        submitTestSelector="QuoteInCartMessageSubmit"
                        dataTestSelector="twoButtonModal_RequiresQuote"
                    ></TwoButtonModal>
                </>
            )}
            {fields.includeActions !== false && canSubmitForQuote && (
                <Button
                    {...styles.submitQuoteButton}
                    disabled={isSubmitForQuoteButtonDisabled}
                    onClick={submitForQuoteClickHandler}
                    data-test-selector="cartTotal_submitQuote"
                    aria-label={submitForQuoteLabel}
                >
                    {submitForQuoteLabel}
                </Button>
            )}
            {/* <CartSaveOrderButton variant="button" extendedStyles={styles.saveOrderButton} /> */}
            {fields.includeActions !== false && canSubmitRequisition && (
                <Button
                    {...styles.submitRequisitionButton}
                    disabled={isSubmitRequisitionButtonDisabled}
                    onClick={submitRequisitionClickHandler}
                    data-test-selector="cartTotal_submitRequisition"
                    aria-label="Submit Requisition"
                >
                    {translate("Submit Requisition")}
                </Button>
            )}
            {/* XNG-CHANGES */}
            {cart?.displayContinueShoppingLink && (
                <Button
                    {...styles.submitQuoteButton}
                    onClick={() => {
                        const defaultUrl = session.dashboardIsHomepage ? myAccountPageUrl : homePageUrl;
                        history.push(defaultUrl!);
                    }}
                    data-test-selector="cartTotal_contineShopping"
                    aria-label="Continue Shopping"
                >
                    {translate("Continue Shopping")}
                </Button>
            )}


            <Modal
                sizeVariant="large"
                headline={translate("Shipping Threshold Warning")}
                handleClose={onClose}
                isOpen={isOpen}
            >
                <StyledWrapper>
                   <Typography>
                        {siteMessage("GUEST_Shipping_Threshold_Warning_Msg",
                            shipmentGroupingInfo?.remainingAmountToFreeShipping?.toString() || "",
                            shipmentGroupingInfo?.freeShippingThreshold?.toString() || "")}
                    </Typography>
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button {...styles.closeButton} variant="secondary" onClick={onClose} aria-label="cancel">
                            {translate("Continue Shopping")}
                        </Button>
                        <Button onClick={checkoutHandler} aria-label="remove">
                            {translate("Continue Checkout")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartTotal)),
    definition: {
        group: "Cart",
        icon: "cart-shopping",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.includeActions,
                displayName: "Include Actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
